import { useStoreState } from '../../../store';
import type { TreeSelectProps } from 'antd';
import { Skeleton } from 'antd';
import { TreeSelect } from 'antd';
import { useCarriers } from '~/hooks/queries/useCarriersFiltered';
import { usePartnerCarrierSummaries } from '~/hooks/queries/usePartnerCarrierSummaries';
import type { CarrierCarrierInfo, UIOrchestrationCarrierSummary } from '@shipium-corp/shipium-ts-types';
import { getReturnsSelectableMethods } from '~/store/assets/carriers/utils';

interface TreeDataNode {
  title: string;
  value: string;
  key: string;
  id?: string;
  selectable?: boolean;
  children?: TreeDataNode[];
}

interface ServiceMethodTreeItem {
  title: string;
  value: string;
  key: string;
  id: string;
}

interface CarrierTreeItem {
  title: string;
  value: string;
  key: string;
  id: string;
  selectable: boolean;
  children: TreeDataNode[];
}

export interface CarrierServiceMethodDropDownProps {
  onChange?: (val: string[]) => void;
  allowedCarriers?: string[];
  excludedCarriers?: string[];
  supportsThirdPartyBilling?: boolean;
  allowedServiceMethodIds?: string[];
  hideCarriers?: boolean;
  isReturnService?: boolean;
  singleSelect?: boolean;
  hideServiceMethods?: boolean;
  disabled?: boolean;
  placeholder?: string;
  onlyConfiguredCarriersAndMethods?: boolean;
  // When set, only service methods that support this origin country (ISO-3166 alpha-2) are shown.
  allowedOriginCountry?: string | null;
}

// A service method supports an origin country when its originCountries list includes it
// (case-insensitive). No filter country → keep all; a method with no origin data → kept, since the
// authoritative check happens server-side at link time.
const methodSupportsOrigin = (
  originCountries: string[] | undefined,
  allowedOriginCountry: string | null | undefined,
): boolean => {
  if (!allowedOriginCountry) return true;
  if (!originCountries?.length) return true;
  return originCountries.some((oc) => oc.toLowerCase() === allowedOriginCountry.toLowerCase());
};

export const CarrierServiceMethodDropDown = ({
  onChange,
  allowedCarriers,
  excludedCarriers,
  supportsThirdPartyBilling,
  allowedServiceMethodIds,
  hideCarriers,
  hideServiceMethods,
  isReturnService = false,
  singleSelect,
  placeholder,
  disabled = false,
  onlyConfiguredCarriersAndMethods = false,
  allowedOriginCountry,
  ...props
}: CarrierServiceMethodDropDownProps) => {
  const { data: carriers = [], isLoading: isLoadingCarriers } = useCarriers();

  const partnerId = useStoreState((state) => state.session.user?.sessionPartnerId);

  const { data: uiPartnerCarrierSummaries = [], isLoading: isLoadingPartnerCarrierSummaries } =
    usePartnerCarrierSummaries({
      partnerId: partnerId || '',
      disabled: !onlyConfiguredCarriersAndMethods,
    });

  const isLoading =
    (!onlyConfiguredCarriersAndMethods && isLoadingCarriers) ||
    (onlyConfiguredCarriersAndMethods && isLoadingPartnerCarrierSummaries);

  const triggerChange = (changedValue: string[]) => {
    onChange?.(changedValue);
  };

  const formatPartnerCarrierSummariesAsTreeData = (carrierList: UIOrchestrationCarrierSummary[]): TreeDataNode[] => {
    return (
      carrierList
        // .filter(val => (!!(val?.thirdPartyCredentialStructures?.length) || !supportsThirdPartyBilling))
        .filter((val) => !allowedCarriers || !!allowedCarriers.find((c) => c === val?.carrierId))
        .filter((val) => !excludedCarriers || !excludedCarriers?.find((c) => c === val?.carrierId))
        .reduce<TreeDataNode[]>((prev, c) => {
          if (hideCarriers) {
            const serviceMethods: ServiceMethodTreeItem[] =
              c.serviceMethods
                ?.filter(
                  (mtd) =>
                    !allowedServiceMethodIds || !!allowedServiceMethodIds.find((id) => id === mtd.serviceMethodId),
                )
                .map(
                  (val): ServiceMethodTreeItem => ({
                    title: val.name || '',
                    value: val.serviceMethodId || '',
                    key: val.serviceMethodId || '',
                    id: val.serviceMethodId || '',
                  }),
                ) || [];

            return [...serviceMethods, ...prev];
          } else {
            const children: TreeDataNode[] = !hideServiceMethods
              ? c.serviceMethods
                  ?.filter(
                    (mtd) =>
                      !allowedServiceMethodIds || !!allowedServiceMethodIds.find((id) => id === mtd.serviceMethodId),
                  )
                  .filter((mtd) => mtd.configured)
                  .map(
                    (val): TreeDataNode => ({
                      title: val.name || '',
                      value: val.serviceMethodId || '',
                      key: val.serviceMethodId || '',
                    }),
                  ) || []
              : [];

            const carrierItem: CarrierTreeItem = {
              title: c.name || '',
              value: c.carrierId || '',
              id: c.carrierId || '',
              key: c.carrierId || '',
              // might need another prop
              // the idea is if you are only selecting
              // one and allowing method selection
              // the one that is select can't be a carrier
              selectable: singleSelect && !hideServiceMethods ? false : true,
              children,
            };

            return [carrierItem, ...prev];
          }
        }, [])
    );
  };
  const formatCarriersAsTreeData = (carrierList: CarrierCarrierInfo[]): TreeDataNode[] => {
    return carrierList
      .filter((val) => !!val?.thirdPartyCredentialStructures?.length || !supportsThirdPartyBilling)
      .filter((val) => !allowedCarriers || !!allowedCarriers.find((c) => c === val?.carrierId))
      .filter((val) => !excludedCarriers || !excludedCarriers?.find((c) => c === val?.carrierId))
      .reduce<TreeDataNode[]>((prev, c) => {
        if (hideCarriers) {
          const serviceMethods: ServiceMethodTreeItem[] = c.serviceMethods
            .filter(
              (mtd) => !allowedServiceMethodIds || !!allowedServiceMethodIds.find((id) => id === mtd.serviceMethodId),
            )
            .filter((mtd) => methodSupportsOrigin(mtd.originCountries, allowedOriginCountry))
            .map(
              (val): ServiceMethodTreeItem => ({
                title: val.name || '',
                value: val.serviceMethodId || '',
                key: val.serviceMethodId || '',
                id: val.serviceMethodId || '',
              }),
            );

          return [...serviceMethods, ...prev];
        } else {
          const children: TreeDataNode[] = !hideServiceMethods
            ? c.serviceMethods
                .filter(
                  (mtd) =>
                    !allowedServiceMethodIds || !!allowedServiceMethodIds.find((id) => id === mtd.serviceMethodId),
                )
                .filter((mtd) => methodSupportsOrigin(mtd.originCountries, allowedOriginCountry))
                .map(
                  (val): TreeDataNode => ({
                    title: val.name || '',
                    value: val.serviceMethodId || '',
                    key: val.serviceMethodId || '',
                  }),
                )
            : [];

          const carrierItem: CarrierTreeItem = {
            title: c.name || '',
            value: c.carrierId || '',
            id: c.carrierId || '',
            key: c.carrierId || '',
            // might need another prop
            // the idea is if you are only selecting
            // one and allowing method selection
            // the one that is select can't be a carrier
            selectable: singleSelect && !hideServiceMethods ? false : true,
            children,
          };

          return [carrierItem, ...prev];
        }
      }, []);
  };

  const formatReturnsServiceMethodsAsTreeData = (carrierList: CarrierCarrierInfo[]): TreeDataNode[] => {
    return carrierList
      .filter((val) => !allowedCarriers || !!allowedCarriers.find((c) => c === val?.carrierId))
      .filter((val) => !excludedCarriers || !excludedCarriers?.find((c) => c === val?.carrierId))
      .reduce<TreeDataNode[]>((prev, c) => {
        // Returns FCs use native return service methods where a carrier has them; carriers without
        // one (e.g. Purolator — returns run the standard/forward path + the G2 push) fall back to
        // forward serviceMethods so they remain selectable. See getReturnsSelectableMethods.
        const returnsServiceMethods: ServiceMethodTreeItem[] = getReturnsSelectableMethods(c)
          .filter((mtd) => methodSupportsOrigin(mtd.originCountries, allowedOriginCountry))
          .map(
            (mtd): ServiceMethodTreeItem => ({
              title: mtd.name || '',
              value: mtd.serviceMethodId || '',
              key: mtd.serviceMethodId || '',
              id: mtd.serviceMethodId || '',
            }),
          );

        return [...returnsServiceMethods, ...prev];
      }, []);
  };

  const treeData = isReturnService
    ? formatReturnsServiceMethodsAsTreeData(Object.values(carriers))
    : onlyConfiguredCarriersAndMethods
      ? formatPartnerCarrierSummariesAsTreeData(uiPartnerCarrierSummaries as UIOrchestrationCarrierSummary[])
      : formatCarriersAsTreeData(Object.values(carriers));

  const defaultPlaceholder = hideCarriers
    ? 'Select Service Methods'
    : hideServiceMethods
      ? 'Select Carriers'
      : 'Select Carriers and/or Methods';

  const tProps: TreeSelectProps = {
    treeDataSimpleMode: true,
    showSearch: true,
    treeData,
    treeCheckable: singleSelect ? false : true,
    multiple: singleSelect ? false : true,
    showCheckedStrategy: 'SHOW_ALL',
    placeholder: placeholder || defaultPlaceholder,
    style: {
      width: '100%',
    },
  };

  if (isLoading || !tProps.treeData) {
    return <Skeleton.Input block active />;
  }

  return (
    <TreeSelect
      loading={isLoading}
      disabled={disabled}
      allowClear={true}
      dropdownMatchSelectWidth={false}
      onChange={triggerChange}
      {...props}
      {...tProps}
    />
  );
};
